/**
 * `/konto` — the trainer's own account, as `profile-web-mockups-v2/f-zakladki.html` draws it. The
 * Kalendarz tab has no handoff (owner, 17 October 2026) and is composed from the same classes.
 *
 * Two columns: a `--bg` sheet holding the header, the tab strip and one scrolling panel, and a
 * `--bg-nav` right column that is NEVER empty — every tab puts its supporting object there, so
 * nothing shifts when the tab changes. Below 1080px the column is hidden outright; the sheet stands
 * alone.
 *
 * The header is the `screenHeader` CLASSES rather than the component, as `/raporty` decided: the
 * component always draws a `<dl>` of statistics and this screen has none.
 *
 * Sign-out lives here rather than in the rail's account menu (owner, 18 September 2026: the avatar
 * navigates and nothing hangs off it), and it asks first, because there is no undo.
 */

import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';

import { screenMotion, useScreenMotionPreference } from '~/shared/design-system/motion';
import { useDocumentTitle } from '~/shared/lib/useDocumentTitle';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import { TabStrip, tabButtonId, tabPanelId } from '~/shared/ui/TabStrip';
import header from '~/shared/ui/screenHeader.module.css';
import shell from '~/shared/ui/canonScreen.module.css';

import { useAccountProfile } from '../lib/useAccountProfile';
import { useLatestAnnouncement } from '../lib/useLatestAnnouncement';
import { useBrandColor } from '../lib/useBrandColor';
import { useEraseAccount } from '../lib/useEraseAccount';
import { useGoogleCalendar } from '../lib/useGoogleCalendar';
import { useSubscriptionCard } from '../lib/useSubscriptionCard';
import { useWebCheckout } from '../lib/useWebCheckout';
import {
  DEFAULT_ACCOUNT_TAB,
  accountTabLabel,
  visibleAccountTabs,
  type AccountTab,
} from '../model/accountTabs';
import { AppTab } from './AppTab';
import { BrandColorTab } from './BrandColorTab';
import { CalendarCard } from './CalendarCard';
import { useAppleCalendar } from '../lib/useAppleCalendar';
import { useStandingCallLink } from '../lib/useStandingCallLink';
import { CalendarTab } from './CalendarTab';
import { EraseAccountDialog } from './EraseAccountDialog';
import { HelpCard } from './HelpCard';
import { PhonePreview } from './PhonePreview';
import { PlanPlate } from './PlanPlate';
import { ProfileTab } from './ProfileTab';
import { SignOutButton } from './SignOutButton';
import { SubscriptionTab } from './SubscriptionTab';
import { TrainerCodeCard } from './TrainerCodeCard';
import styles from './AccountScreen.module.css';

interface AccountScreenProps {
  readonly tab: AccountTab;
  readonly onSelectTab: (tab: AccountTab) => void;
}

export function AccountScreen({ tab: asked, onSelectTab }: AccountScreenProps) {
  const { t } = useTranslation();
  const reducedMotion = useScreenMotionPreference();
  useDocumentTitle(t('web:account.title'));

  /* Read whichever tab is open: where Google is not offered there is no Kalendarz tab, and an
   * address asking for it shows Profil. */
  const calendar = useGoogleCalendar(asked === 'integracje');
  const tab: AccountTab = asked === 'integracje' && !calendar.shown ? DEFAULT_ACCOUNT_TAB : asked;

  /**
   * The Profil tab's read is made HERE rather than inside the tab, because the trainer's code —
   * which the right column draws — rides on the same response. Two reads of one row would be two
   * copies of it in the cache with nothing keeping them in step.
   */
  const profile = useAccountProfile(tab === 'profil');
  /* Each tab's reads are made only for the tab that draws them. The hooks are called
   * unconditionally — they are hooks — and each one's query carries the predicate, which is also
   * what keeps a disabled query's permanent `isPending` out of the skeleton gates. */
  const brand = useBrandColor(tab === 'kolor-marki');
  const subscription = useSubscriptionCard(tab === 'subskrypcja');
  /* The tab's second half: the catalogue, the two redirects and the wait after a payment. It is a
   * hook of its own rather than more of `useSubscriptionCard`, because the two answer different
   * questions — what the account HAS, and what it may buy — and only one of them has a clock. */
  const checkout = useWebCheckout(tab === 'subskrypcja');
  /* Integracje's two other blocks: the Apple Calendar button and the standing call link. */
  const appleCalendar = useAppleCalendar(tab === 'integracje');
  const standingLink = useStandingCallLink(tab === 'integracje');

  /* The screen's one live region, fed by every tab that speaks. See `useLatestAnnouncement` for
   * why this is not `a || b || c`: `useAnnouncer` never clears, so the first source to speak owned
   * the region for the rest of the session and the checkout's own sentences were announced to
   * nobody. The trial code's line is the card's new button and fact, said once when the code lands
   * (R19). */
  const announcement = useLatestAnnouncement([
    profile.announcement,
    brand.announcement,
    checkout.notice ?? '',
    checkout.trialAnnouncement,
    calendar.announcement,
    appleCalendar.announcement,
    standingLink.announcement,
  ]);

  /* The erase dialog is raised from the Profil tab and returns focus to the link that raised it. */
  const [erasing, setErasing] = useState(false);
  const eraseLink = useRef<HTMLButtonElement>(null);
  const erasure = useEraseAccount(erasing);

  /**
   * A TABLE keyed by the tab rather than a chain of ternaries: `ACCOUNT_TABS` is the declared source
   * of truth for which tabs exist, and `Record<AccountTab, …>` makes a sixth tab a compile error
   * instead of a silent `else`. ONE table rather than two, because a tab's panel and the object in
   * the column beside it are two halves of one tab and change together.
   */
  const tabs: Readonly<
    Record<AccountTab, { panel: () => React.JSX.Element; side: () => React.JSX.Element }>
  > = {
    profil: {
      panel: () => (
        <ProfileTab
          profile={profile}
          eraseRef={eraseLink}
          onRequestErase={() => {
            setErasing(true);
          }}
        />
      ),
      side: () => <TrainerCodeCard profile={profile} />,
    },
    'kolor-marki': {
      panel: () => <BrandColorTab brand={brand} />,
      side: () => <PhonePreview brand={brand} />,
    },
    subskrypcja: {
      panel: () => <SubscriptionTab subscription={subscription} checkout={checkout} />,
      side: () => <PlanPlate subscription={subscription} />,
    },
    integracje: { panel: () => <CalendarTab calendar={calendar} apple={appleCalendar} standing={standingLink} />, side: () => <CalendarCard /> },
    aplikacja: { panel: () => <AppTab />, side: () => <HelpCard /> },
  };

  return (
    <main className={shell.page}>
      <div className={styles.columns}>
        <div className={styles.sheet}>
          <div className={styles.header}>
            <div className={header.titleRow}>
              {/* `AppShell` finds the screen's `h1` and moves focus to it after a route change, so
                * it is rendered in every state rather than waiting for a read. */}
              <h1 className={header.title}>{t('web:account.title')}</h1>

              <SignOutButton />
            </div>
          </div>

          <TabStrip
            className={styles.tabBar}
            label={t('web:account.tabsLabel')}
            tabs={visibleAccountTabs(calendar.shown)}
            selected={tab}
            renderLabel={(entry) => accountTabLabel(entry, t)}
            onSelect={onSelectTab}
          />

          {/* `tabIndex={0}` on the panel, on every tab rather than on the ones that need it: the
            * tabs pattern requires it wherever a panel holds nothing focusable, and the alternative
            * is a rule that watches its own children.
            *
            * The `key` is what replays the entrance — a new key is a new element, and the element
            * is the tab's content, which genuinely changed. */}
          <motion.div
            key={tab}
            className={styles.panel}
            role="tabpanel"
            id={tabPanelId(tab)}
            aria-labelledby={tabButtonId(tab)}
            tabIndex={0}
            {...screenMotion('rise', { reducedMotion })}
          >
            {tabs[tab].panel()}
          </motion.div>
        </div>

        {/* The `aria-label` names the column for what it holds rather than „panel": three
          * complementary landmarks already share other screens in this app, and „Obok Konta" is not
          * something a listener can act on. */}
        <aside className={styles.side} aria-label={accountTabLabel(tab, t)}>
          {tabs[tab].side()}
        </aside>
      </div>

      {/* Always mounted and empty when there is nothing to say — a `role="status"` inserted with its
        * text is a region that did not exist when the change happened. There is no toast in this app.
        *
        * The checkout's own notice goes through the SAME region rather than a second one: „nie
        * doszło do zakupu", „aktywujemy…" and the sentence that replaces it when the minute runs out
        * are the answer to the thing the trainer just did, and they were drawn in warn ink and
        * announced to nobody. One region, because two live regions on one screen race each other,
        * and each message replaces the last — which is exactly the shape of this wait: one sentence
        * at a time, changing as the phase does. */}
      <LiveRegionAnnouncer message={announcement} />

      <EraseAccountDialog
        open={erasing}
        erasure={erasure}
        finalFocus={eraseLink}
        onClose={() => {
          setErasing(false);
        }}
      />

    </main>
  );
}
